import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [leagues, setLeagues] = useState([]);
  const [observed, setObserved] = useState([]);
  const [leaguesLoaded, setLeaguesLoaded] = useState(false);
  const [config, setConfig] = useState(null);
  const [currentLeagueId, setCurrentLeagueIdState] = useState(() => localStorage.getItem("mpl_league"));

  const loadLeagues = useCallback(async () => {
    try {
      const { data } = await api.get("/leagues");
      setLeagues(data.filter((l) => !l.observer));
      setObserved(data.filter((l) => l.observer));
      setLeaguesLoaded(true);
      return data;
    } catch (e) {
      return [];
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      if (data.role !== "partner") await loadLeagues();
      return data;
    } catch (e) {
      if (!e?.response || e.response.status >= 500) {
        setTimeout(() => refreshUser(), 4000);
        return null;
      }
      setUser(false);
      return false;
    }
  }, [loadLeagues]);

  useEffect(() => {
    refreshUser();
    api.get("/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, [refreshUser]);

  const setCurrentLeagueId = (id) => {
    localStorage.setItem("mpl_league", id);
    setCurrentLeagueIdState(id);
  };

  const [preview, setPreviewState] = useState(() => localStorage.getItem("mpl_theme_preview"));
  const setThemePreview = (t) => {
    if (t) localStorage.setItem("mpl_theme_preview", t); else localStorage.removeItem("mpl_theme_preview");
    setPreviewState(t);
  };
  const globalTheme = config?.theme || localStorage.getItem("mpl_theme_global") || "classic";
  const theme = user?.role === "superadmin" && preview ? preview : globalTheme;
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", theme === "vestiaire" ? "#101310" : "#0B1020");
  }, [theme]);
  useEffect(() => {
    if (config?.theme) localStorage.setItem("mpl_theme_global", config.theme);
  }, [config?.theme]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("mpl_league");
    localStorage.removeItem("mpl-tenant");
    localStorage.removeItem("mpl-tenant-auto");
    setLeagues([]);
    setUser(false);
  };

  const ids = leagues.map((l) => l.id);
  const openIds = leagues.filter((l) => !l.archived).map((l) => l.id);
  const leagueId = ids.includes(currentLeagueId)
    ? currentLeagueId
    : ids.includes(user?.primary_league_id) ? user.primary_league_id : openIds[0] || ids[0] || null;
  const currentLeague = leagues.find((l) => l.id === leagueId) || null;

  useEffect(() => {
    if (user && leaguesLoaded && currentLeagueId && !ids.includes(currentLeagueId)) {
      localStorage.removeItem("mpl_league");
      setCurrentLeagueIdState(null);
    }
  }, [user, leaguesLoaded, currentLeagueId, ids.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AuthContext.Provider
      value={{ user, setUser, refreshUser, logout, leagues, observed, loadLeagues, config, leagueId, currentLeague, setCurrentLeagueId, tz: user?.timezone, theme, globalTheme, preview, setThemePreview,
        reloadConfig: () => api.get("/config").then(({ data }) => setConfig(data)) }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
